import { useState } from 'react';
import { trendingPosts, trendingTabs, type TrendingPost } from '@/mocks/home';
import SectionHeading from '@/pages/home/components/SectionHeading';
import Reveal from '@/components/base/Reveal';

const kindStyles: Record<TrendingPost['kind'], string> = {
  hot: 'bg-primary-100 text-primary-800',
  expose: 'bg-accent-100 text-accent-900',
  dispute: 'bg-secondary-100 text-secondary-900',
};

function LargeCard({ post }: { post: TrendingPost }) {
  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:col-span-2 md:row-span-2">
      <div className="h-[220px] w-full overflow-hidden md:h-[300px]">
        <img
          src={post.image}
          alt={post.title}
          title={`${post.title} 黑料社区热帖`}
          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${kindStyles[post.kind]}`}>{post.tag}</span>
          <span className="flex items-center gap-1 text-xs text-foreground-500">
            <i className="ri-fire-line text-primary-600" />
            热度 {post.heat}
          </span>
          <span className="text-xs text-foreground-400">{post.time}</span>
        </div>
        <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-2xl">
          {post.title}
        </h3>
        <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-600">{post.summary}</p>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
          <span className="flex items-center gap-2 text-xs text-foreground-600">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background-200 text-[11px] font-medium text-foreground-700">
              {post.author.slice(0, 1)}
            </span>
            {post.author}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-foreground-600">
            <i className="ri-chat-3-line" />
            {post.replies} 条跟帖
          </span>
        </div>
      </div>
    </article>
  );
}

function SmallCard({ post }: { post: TrendingPost }) {
  return (
    <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${kindStyles[post.kind]}`}>{post.tag}</span>
          <span className="text-[11px] text-foreground-400">{post.time}</span>
        </div>
        <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {post.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{post.summary}</p>
        <div className="mt-auto flex items-center gap-3 pt-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-600" />
            {post.heat}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line" />
            {post.replies}
          </span>
          <span className="truncate">{post.author}</span>
        </div>
      </div>
      {post.image && (
        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md border border-background-200">
          <img src={post.image} alt={post.title} title={`${post.title} 黑料社区爆料帖`} className="h-full w-full object-top" />
        </div>
      )}
    </article>
  );
}

export default function TrendingSection() {
  const [activeTab, setActiveTab] = useState(trendingTabs[0]);

  const filtered = trendingPosts.filter((post) => {
    if (activeTab === '全部动态') return true;
    if (activeTab === '今日热帖') return post.kind === 'hot';
    if (activeTab === '爆料帖') return post.kind === 'expose';
    if (activeTab === '争议反转') return post.kind === 'dispute';
    return true;
  });

  const lead = filtered[0];
  const rest = filtered.slice(1);

  return (
    <section id="trending" className="w-full bg-background-100 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1360px] px-4 md:px-6">
        <SectionHeading
          kicker="今日热帖 · 爆料帖"
          title="大小卡混排的动态墙"
          desc="热度高的排进大卡，更新快的走小卡。今日热帖与爆料帖混在一起，标签一换就能只看自己关心的那一类。"
          actionLabel="查看全部动态"
        />

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {trendingTabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`cursor-pointer whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center text-sm text-foreground-500">
            这个分类暂时没有新帖，换个标签看看。
          </p>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4 md:gap-5">
            {lead && (
              <Reveal className="h-full md:col-span-2 md:row-span-2">
                <LargeCard post={lead} />
              </Reveal>
            )}
            {rest.map((post, index) => (
              <Reveal key={post.id} className="h-full md:col-span-2" delay={80 * (index + 1)}>
                <SmallCard post={post} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}